import { useState } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { appsApi, errMsg, inputCls, btnDark } from "@/lib/appsApi";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export const AppsSignIn = ({ onSignedIn, initialError = "" }) => {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(initialError);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/apps";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (mode === "signin") {
        const r = await appsApi.post("/auth/login", { email, password });
        onSignedIn(r?.data ?? null);
      } else {
        const r = await appsApi.post("/auth/forgot", { email });
        setNotice(r?.data?.message ?? "Check your email.");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const forgot = mode === "forgot";
  return (
    <div data-testid="apps-signin">
      <h2 className="font-display text-3xl font-semibold tracking-tight text-ink">{forgot ? "Set or reset password" : "Tester sign in"}</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {forgot ? "Enter the email an admin added. If it's on the list, we'll email you a link." : "Only emails added by an admin can sign in."}
      </p>
      {!forgot && (
        <>
          <button onClick={google} className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-medium text-ink transition-colors hover:border-sky-600" data-testid="apps-google-button">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
        </>
      )}
      <form onSubmit={submit} className={`space-y-4 ${forgot ? "mt-8" : ""}`} data-testid="apps-password-form">
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="apps-email-input" />
        {!forgot && <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="apps-password-input" />}
        {error && <p className="text-sm text-red-600" data-testid="apps-signin-error">{error}</p>}
        {notice && <p className="rounded-2xl bg-sky-50 p-4 text-sm text-sky-900" data-testid="apps-signin-notice">{notice}</p>}
        <button type="submit" disabled={busy} className={`${btnDark} w-full`} data-testid="apps-signin-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {forgot ? "Email me a link" : "Sign in with password"}
        </button>
      </form>
      <button onClick={() => { setMode(forgot ? "signin" : "forgot"); setError(""); setNotice(""); }} className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-900" data-testid="apps-toggle-forgot">
        {forgot ? <><ArrowLeft className="h-4 w-4" /> Back to sign in</> : "Forgot password or first time? Set a password"}
      </button>
    </div>
  );
};
